import {
  convertDateToYYYYMMDDFormat,
  CustomDateRangePicker,
  DataTable,
  PageWrapper,
  parseLocalDateFromYYYYMMDD,
  STATUSPAGE,
} from '@repo/ui';
import {
  AcademicManagementGroup,
  AcademicManagementInternshipLocation,
  AcademicManagementProfessional,
  AcademicManagementReportType,
  AcademicManagementTask,
} from 'domain/models';
import { FormikValues } from 'formik';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { AcademicOverviewSectionState } from 'presentation/config/store/reports/academic-overview-slice';
import { ReactNode } from 'react';
import { TableColumn } from 'react-data-table-component';
import {
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Label,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  TabContent,
  TabPane,
  UncontrolledDropdown,
} from 'reactstrap';

import {
  makeAcademicManagementGroupColumns,
  makeAcademicManagementInternshipLocationColumns,
  makeAcademicManagementProfessionalColumns,
  makeAcademicManagementTaskColumns,
} from '../../common/columns/academic-overview/academic-management-columns';

interface IProps {
  activeTab: AcademicManagementReportType;
  exportingReport?: AcademicManagementReportType;
  groups: AcademicOverviewSectionState<AcademicManagementGroup>;
  tasks: AcademicOverviewSectionState<AcademicManagementTask>;
  internshipLocations: AcademicOverviewSectionState<AcademicManagementInternshipLocation>;
  professionals: AcademicOverviewSectionState<AcademicManagementProfessional>;
  validation: FormikValues;
  onTabChange: (tab: AcademicManagementReportType) => void;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (length: number, page: number) => void;
  onSearch: (value: string) => void;
  onExport: (report: AcademicManagementReportType) => void;
}

type ReportTableProps<T extends object> = {
  columns: TableColumn<T>[];
  section: AcademicOverviewSectionState<T>;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (length: number, page: number) => void;
  onSearch: (value: string) => void;
};

const ReportTable = <T extends object>({
  columns,
  section,
  onChangePage,
  onChangeRowsPerPage,
  onSearch,
}: ReportTableProps<T>) => {
  if (!section.loadedForFiltersKey && section.status !== STATUSPAGE.LOADING) {
    return (
      <div className="py-5 text-center text-muted">
        Selecione os filtros e clique em Consultar para carregar os dados.
      </div>
    );
  }

  return (
    <DataTable
      canSearch
      searchServer
      pagination
      paginationServer
      actions={[]}
      columns={columns}
      data={section.data}
      loading={section.status === STATUSPAGE.LOADING}
      paginationTotalRows={section.total}
      paginationDefaultPage={section.page}
      defaultRowsPerPage={section.length}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onSearch={onSearch}
    />
  );
};

const tabs: Array<{
  id: AcademicManagementReportType;
  label: string;
}> = [
  { id: 'groups', label: 'Grupos' },
  { id: 'tasks', label: 'Tarefas' },
  { id: 'internship-locations', label: 'Locais de Estágio' },
  { id: 'professionals', label: 'Profissionais' },
];

export const AcademicOverviewView = ({
  activeTab,
  exportingReport,
  groups,
  tasks,
  internshipLocations,
  professionals,
  validation,
  onTabChange,
  onChangePage,
  onChangeRowsPerPage,
  onSearch,
  onExport,
}: IProps) => {
  const isActiveTabLoading =
    (activeTab === 'groups' && groups.status === STATUSPAGE.LOADING) ||
    (activeTab === 'tasks' && tasks.status === STATUSPAGE.LOADING) ||
    (activeTab === 'internship-locations' &&
      internshipLocations.status === STATUSPAGE.LOADING) ||
    (activeTab === 'professionals' &&
      professionals.status === STATUSPAGE.LOADING);

  const renderExportItem = (
    report: AcademicManagementReportType,
    label: ReactNode,
  ) => (
    <DropdownItem
      key={report}
      disabled={Boolean(exportingReport)}
      onClick={() => onExport(report)}
    >
      <i className="ri-file-excel-2-line me-2" />
      {label}
    </DropdownItem>
  );

  return (
    <PageWrapper title="Gestão Acadêmica">
      <div className="mb-4">
        <h4>Gestão Acadêmica</h4>
        <p className="text-muted mb-0">
          Acompanhe indicadores e métricas gerais sobre grupos, estudantes e
          atividades acadêmicas.
        </p>
      </div>

      <Card className="mb-4">
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <Col md={9} lg={6} xxl={4}>
              <Label htmlFor="initial-date" className="form-label">
                Período:
              </Label>
              <CustomDateRangePicker
                initialStartDate={parseLocalDateFromYYYYMMDD(
                  validation.values.starts,
                )}
                initialEndDate={parseLocalDateFromYYYYMMDD(
                  validation.values.ends,
                )}
                placement="auto"
                onDateChange={(start, end) => {
                  validation.setFieldValue(
                    'starts',
                    convertDateToYYYYMMDDFormat(start),
                  );
                  validation.setFieldValue(
                    'ends',
                    convertDateToYYYYMMDDFormat(end),
                  );
                }}
              />
            </Col>
          }
          buttonsChildren={
            <Button type="submit" color="primary" disabled={isActiveTabLoading}>
              {isActiveTabLoading ? (
                <Spinner size="sm">Carregando...</Spinner>
              ) : (
                <>
                  Consultar
                  <i className="ri-search-line ms-2" />
                </>
              )}
            </Button>
          }
        />
      </Card>

      <Card>
        <CardBody>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <Nav tabs>
              {tabs.map((tab) => (
                <NavItem key={tab.id}>
                  <NavLink
                    active={activeTab === tab.id}
                    role="button"
                    onClick={() => onTabChange(tab.id)}
                  >
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <UncontrolledDropdown>
              <DropdownToggle
                caret
                color="primary"
                outline
                disabled={Boolean(exportingReport)}
              >
                {exportingReport ? (
                  <Spinner size="sm" className="me-2">
                    Exportando...
                  </Spinner>
                ) : (
                  <i className="ri-download-2-line me-2" />
                )}
                Exportar
              </DropdownToggle>
              <DropdownMenu end>
                {tabs.map((tab) => renderExportItem(tab.id, tab.label))}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="groups">
              {activeTab === 'groups' && (
                <ReportTable
                  columns={makeAcademicManagementGroupColumns()}
                  section={groups}
                  onChangePage={onChangePage}
                  onChangeRowsPerPage={onChangeRowsPerPage}
                  onSearch={onSearch}
                />
              )}
            </TabPane>
            <TabPane tabId="tasks">
              {activeTab === 'tasks' && (
                <ReportTable
                  columns={makeAcademicManagementTaskColumns()}
                  section={tasks}
                  onChangePage={onChangePage}
                  onChangeRowsPerPage={onChangeRowsPerPage}
                  onSearch={onSearch}
                />
              )}
            </TabPane>
            <TabPane tabId="internship-locations">
              {activeTab === 'internship-locations' && (
                <ReportTable
                  columns={makeAcademicManagementInternshipLocationColumns()}
                  section={internshipLocations}
                  onChangePage={onChangePage}
                  onChangeRowsPerPage={onChangeRowsPerPage}
                  onSearch={onSearch}
                />
              )}
            </TabPane>
            <TabPane tabId="professionals">
              {activeTab === 'professionals' && (
                <ReportTable
                  columns={makeAcademicManagementProfessionalColumns()}
                  section={professionals}
                  onChangePage={onChangePage}
                  onChangeRowsPerPage={onChangeRowsPerPage}
                  onSearch={onSearch}
                />
              )}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </PageWrapper>
  );
};
